import React from 'react';

import { FlexColumn, FlexRow } from '@editor/components/utilities/layouts';
import { useRevampedCheckoutFlowVariant } from '@editor/global/growthbookUtils';
import { withEventScope } from '@repo/event-processor';
import {
  subscriptionStoreSelector,
  useSubscriptionStore,
} from '@repo/subscription/client';
import { Button, primaryTheme } from '@repo/ui/components/Buttons';
import PremiumBadgeIcon from '@repo/ui/components/Icon/PremiumBadgeIcon';
import { Spacer } from '@repo/ui/components/Spacer';
import { H2, H5, P1, P2, P3 } from '@repo/ui/components/Typography';
import { useNewThemeActive } from '@repo/ui/theming';

import { HideDesktop, HideMobile } from '../../utilities/queries';
import usePaymentSuccessModalData from '../hooks/usePaymentSuccessModalData';

import FeatureItem from './FeatureItem';
import { LeftColumn, RightColumn, ImageWrapper, Link } from './styles';
import { SubscriptionSuccessModalContentProps } from './types';
import useSubscriptionSuccessTracking from './useSubscriptionSuccessTracking';

const SubscriptionSuccessContent: React.FC<
  SubscriptionSuccessModalContentProps
> = ({ onContinueClick, subscription, plan, singlePayment }) => {
  const showRevampedCheckoutFlow = useRevampedCheckoutFlowVariant() !== 'off';

  const data = usePaymentSuccessModalData({
    subscription,
    plan,
    singlePayment,
  });
  useSubscriptionSuccessTracking(singlePayment);
  const openBillingPortal = useSubscriptionStore(
    subscriptionStoreSelector.openBillingPortal
  );

  const isNewThemeActive = useNewThemeActive();

  return (
    <>
      <LeftColumn>
        <div>
          {!showRevampedCheckoutFlow && data.badge && (
            <FlexRow alignItems="center" gap="8px">
              <PremiumBadgeIcon height="20px" />
              <H5>{data.badge}</H5>
            </FlexRow>
          )}
          <Spacer height="12px" />
          <H2>{data.title}</H2>
          {!showRevampedCheckoutFlow && data.lead && (
            <>
              <Spacer height="12px" />
              <P1>{data.lead}</P1>
            </>
          )}
          <Spacer height={showRevampedCheckoutFlow ? '16px' : '12px'} />
          <P2>{data.description}</P2>
          <Spacer height="24px" />
          {!showRevampedCheckoutFlow && (
            <FlexColumn gap="16px">
              {data.mainFeatures && (
                <>
                  <H5 style={{ textAlign: 'left' }}>{data.featuresLabel}</H5>
                  {data.mainFeatures.map((feature, idx) => (
                    <FeatureItem
                      key={idx}
                      isBold={feature.bold}
                      isPremium={feature.premium}
                      text={feature.label}
                    />
                  ))}
                </>
              )}
              {data.otherFeatures?.map((feature, idx) => (
                <FeatureItem
                  key={idx}
                  isPremium={feature.premium}
                  text={feature.label}
                />
              ))}
            </FlexColumn>
          )}
        </div>
        {showRevampedCheckoutFlow ? (
          <Button
            label={data.buttonLabel}
            name="continueSubscriptionButton"
            theme={{ ...primaryTheme, padding: '12px' }}
            width="unset"
            onClick={onContinueClick}
          />
        ) : (
          <div>
            <Spacer height="24px" />
            <HideDesktop>
              <Button
                label={data.buttonLabel}
                name="subscriptionSuccessContinueButton"
                width="100%"
                onClick={onContinueClick}
              />
            </HideDesktop>
            <HideMobile>
              <Button
                label={data.buttonLabel}
                name="continueSubscriptionButton"
                width="auto"
                onClick={onContinueClick}
              />
            </HideMobile>
            <Spacer height="24px" />
            <P3>
              Visit{' '}
              <Link target="_blank" onClick={openBillingPortal}>
                Payment & Billing
              </Link>{' '}
              to manage your subscription
            </P3>
          </div>
        )}
      </LeftColumn>
      <RightColumn>
        <ImageWrapper
          background={data.imageUrl}
          isNewThemeActive={isNewThemeActive}
        />
      </RightColumn>
    </>
  );
};

export default withEventScope(SubscriptionSuccessContent, {
  feature: 'subscriptionSuccessContent',
});
